import { Button, Text } from "@athena/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { setListMode, type ListMode, type ListView } from "@/hooks/useListView";

/**
 * The scroll/pages choice, and the pager it turns on.
 *
 * Sits above the table it belongs to. The choice itself is portal-wide — see `useListView` — so
 * this renders the same state on every table and changing it here changes it everywhere.
 */

const MODES: { value: ListMode; label: string; hint: string }[] = [
    { value: "scroll", label: "Scroll", hint: "Load more rows as you reach the bottom" },
    { value: "pages", label: "Pages", hint: "Move through the list a page at a time" },
];

interface ListViewControlsProps {
    view: ListView<unknown>;
    /** What the rows are, for the pager's count. Plural, lowercase — "users", "videos". */
    noun: string;
    /**
     * How much of the list is on screen — "21–33 of 33".
     *
     * Rendered here rather than beside the search box, so the row reads left to right as the
     * choice, the position, and the extent of it. Optional: the surfaces whose count lives in
     * `FilterBar` pass nothing and the slot stays empty, which still balances the pager.
     */
    count?: string;
}

export function ListViewControls({ view, noun, count }: ListViewControlsProps) {
    // Nothing to choose between on a list that fits on one page — see `canPage`.
    if (!view.canPage) return null;

    return (
        /*
         * Three equal columns, so the pager sits on the middle of the row rather than the middle
         * of whatever space the other two happen to leave. `mb-4` is the gap to the table: the
         * control belongs to the table under it, but touching it read as part of the header.
         */
        <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="flex flex-1 justify-start">
                <div className="border-input inline-flex items-center rounded-md border p-0.5" role="group">
                    {MODES.map((mode) => (
                        <Button
                            key={mode.value}
                            size="xs"
                            variant={view.mode === mode.value ? "secondary" : "ghost"}
                            aria-pressed={view.mode === mode.value}
                            title={mode.hint}
                            onClick={() => setListMode(mode.value)}
                        >
                            {mode.label}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="flex flex-1 justify-center">
                {view.mode === "pages" ? (
                    <div className="flex items-center gap-2">
                        <Button
                            size="xs"
                            variant="outline"
                            disabled={!view.hasPrev}
                            onClick={view.toPrev}
                            aria-label={`Previous page of ${noun}`}
                            leftIcon={<ChevronLeft className="h-3.5 w-3.5" />}
                        >
                            Previous
                        </Button>
                        {/*
                         * The page number, not "page 3 of 47". A total would need a count query on
                         * every table, and the pages themselves are fetched lazily — the honest
                         * thing to show is where the reader is and whether there is more.
                         *
                         * `whitespace-nowrap`: without it "Page 1" breaks after "Page" when tight.
                         */}
                        <Text size="sm" muted aria-live="polite" className="whitespace-nowrap">
                            Page {view.page + 1}
                        </Text>
                        <Button
                            size="xs"
                            variant="outline"
                            disabled={!view.hasNext}
                            onClick={view.toNext}
                            aria-label={`Next page of ${noun}`}
                            rightIcon={<ChevronRight className="h-3.5 w-3.5" />}
                        >
                            Next
                        </Button>
                    </div>
                ) : null}
            </div>

            <div className="flex flex-1 justify-end">
                {count ? (
                    <Text size="sm" muted className="whitespace-nowrap">
                        {count}
                    </Text>
                ) : null}
            </div>
        </div>
    );
}
